import { Injectable } from "@angular/core";

import {
  RatePromiseService,
  SystemConfigPromiseService,
  OrdersTableDefinitionFactory,
  OrderDataPromiseLookupService,
  TableDefinition,
  SysFeatures,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  RateCache,
  Rate,
  ActiveUserProfilePromiseService,
  ActiveUser,
} from "shared/cubiq-services";

import { FixedIndexRow } from "shared/cubiq-services/model/FixedIndexRow";

import { Subject } from "rxjs";

import { BfxBaseBlotterService } from "../bfx-base-blotter/bfx-base-blotter.service";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

// Delaying the subscription lowers the memory footprint and the strain on the garbage collection
export const DELAY_SUBSCRIBE_FOR_RATES: number = 3000;
export const COLUMN_PREV_TEMPERATURE: string = "prevTemp";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { MARKET_BAR_ORDER_ID_KEY } from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { LimitOrder } from "shared/cubiq-services/model/orders/LimitOrder";

const INVISIBLE_STATES = [CubiqTS.OrderState.GAMMA_MID_PENDING];

const TERMINAL_STATES = [CubiqTS.OrderState.FILLED, CubiqTS.OrderState.EXPIRED];

@Injectable()
export class BfxBlotterOrdersService extends BfxBaseBlotterService {
  protected static ratesPaused: boolean = false;
  protected static rateResumeTimer: any = null;
  protected static rateResumeCountdown: any = null;
  protected static ratePauseTimeout: number = 30;
  protected static rateResumeCounter: number = 0;
  protected static marketBarEnabled: boolean = true;
  protected queuedRates: Map<string, Rate> = new Map();
  protected rateProcessingBusy: boolean = false;
  protected _pairOrderIds: Map<string, Set<string>> = new Map();

  public static rateStatusSubject: Subject<boolean> = new Subject();
  public partialUpdateSubject: Subject<Array<any>> = new Subject<Array<any>>();

  public marketBar: any = null;
  protected activeUser: ActiveUser;

  constructor(
    protected rateService: RatePromiseService,
    protected rateCache: RateCache,
    protected systemConfigService: SystemConfigPromiseService,
    public dataService: OrderDataPromiseLookupService,
    public tableDefinitionFactory: OrdersTableDefinitionFactory,
    protected profileActionService: ProfilePromiseActionService,
    protected activeUserService: ActiveUserProfilePromiseService,
  ) {
    super(dataService, tableDefinitionFactory);
    this.activeUserService.getActiveUserData().then((value) => {
      this.activeUser = value;
    });

    this.systemConfigService
      .getSystemConfig(SysFeatures.pauseRatesTimeOutSecs)
      .then((value: string) => {
        if (value != null) {
          try {
            BfxBlotterOrdersService.ratePauseTimeout = parseInt(value);
          } catch (e) {
            logger.out.error(
              "Invalid value for " +
                SysFeatures.pauseRatesTimeOutSecs +
                ": " +
                value +
                ". Expected number of seconds.",
            );
          }
        }

        this.profileActionService
          .getProfileConfig(ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences)
          .then((config) => {
            if (config) {
              this.setUserSettingsBlotterPreferences(config);
            }
          });
        profileActionService
          .getProfileConfigUpdates(ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences)
          .subscribe((profileValue) => {
            this.setUserSettingsBlotterPreferences(profileValue);
            this.updateMarketBarStatus();
          });
      });

    this.rateService.isStreamingPaused().then((paused: boolean) => {
      BfxBlotterOrdersService.ratesPaused = paused;
    });
  }

  protected onDeleteEvent(rowId: string): void {
    super.onDeleteEvent(rowId);
  }

  // override this in ladder service to update the ladder properties
  protected postProcessPartUpdate(row): void {}

  public pauseRates(): void {
    this.rateService.pauseStream();
    BfxBlotterOrdersService.ratesPaused = true;
    BfxBlotterOrdersService.rateStatusSubject.next(!BfxBlotterOrdersService.ratesPaused);
    BfxBlotterOrdersService.rateResumeCounter = BfxBlotterOrdersService.ratePauseTimeout;
    BfxBlotterOrdersService.rateResumeTimer = setTimeout(
      this.resumeRates.bind(this),
      BfxBlotterOrdersService.ratePauseTimeout * 1000,
    );
    BfxBlotterOrdersService.rateResumeCountdown = setInterval(
      this.countdownTimeout.bind(this),
      1000,
    );
  }

  public resumeRates(): void {
    this.rateService.resumeStream();

    if (BfxBlotterOrdersService.rateResumeTimer != null) {
      clearTimeout(BfxBlotterOrdersService.rateResumeTimer);
      clearInterval(BfxBlotterOrdersService.rateResumeCountdown);
      BfxBlotterOrdersService.rateResumeTimer = null;
    }

    BfxBlotterOrdersService.ratesPaused = false;
    BfxBlotterOrdersService.rateStatusSubject.next(!BfxBlotterOrdersService.ratesPaused);
  }

  public areRatesPaused(): boolean {
    return BfxBlotterOrdersService.ratesPaused;
  }

  public getRateResumeCounter(): number {
    return BfxBlotterOrdersService.rateResumeCounter;
  }

  protected triggerResumeRate(): void {
    if (BfxBlotterOrdersService.ratesPaused) {
      this.resumeRates();
    }
  }

  protected countdownTimeout(): void {
    BfxBlotterOrdersService.rateResumeCounter--;
  }

  protected acceptsRow(row: any): boolean {
    return row?.orderType !== CubiqTS.OrderType.ALGO && row?.orderType !== CubiqTS.OrderType.BMALGO;
  }

  protected addOrUpdateRow(row: any): any {
    if (this.acceptsRow(row)) {
      return this.addOrUpdateBlotterRow(row) ?? null;
    }
  }

  protected addOrUpdateBlotterRow(row: any): any {
    if (row?.isMarketBar) {
      this._rawData[row[this._tableDefinition.primaryKey]] = row;
      return null;
    }

    if (null != row && null != row.orderId) {
      let pairKey: string = row.baseCode + row.nonBaseCode;
      let orderKey: string = row.orderId.toString();
      let pairSet = this._pairOrderIds.get(pairKey);

      if (this._pairOrderIds.get(pairKey) == undefined) {
        pairSet = new Set();
        this._pairOrderIds.set(pairKey, pairSet);
      }

      if (!pairSet.has(orderKey)) {
        pairSet.add(orderKey);
      }

      if (this.activeUser != null) {
        let isBlotterOrder = this.activeUser.canSeeOrderInBlotter(row);
        let limitOrder = new LimitOrder(row, isBlotterOrder);
        this._rawData[row[this._tableDefinition.primaryKey]] = limitOrder;
        return limitOrder;
      }
    }
  }

  protected onInitialPrime(rows: Array<any>): void {
    let colDef = this._tableDefinition.getColumnDefinition("dealtPercentAway");

    if (
      colDef &&
      colDef.fixedIndexRows &&
      colDef.fixedIndexRows.length > 0 &&
      BfxBlotterOrdersService.marketBarEnabled
    ) {
      this.marketBar = colDef.fixedIndexRows[0];
      this.marketBar["fullWidth"] = true;
      rows.unshift(this.marketBar);
    }

    this.activeUserService.getActiveUserData().then((value: ActiveUser) => {
      super.onInitialPrime(rows);
      this.processRates(this.rateCache.getAllRates());
      this.rateService.getRateEventSubject().subscribe(this.processRates.bind(this));
    });
  }

  /**
   * Processes rates delivered from the rate service. It involves marking affected
   * orders by putting them in the queue for batched partial update.
   * @param rates the updated rates
   * @param nonOrderRows optional parameter that can contain additional non-order rows.
   * Will contain all rows that will be updated when finished.
   */
  protected processRates(rates: Array<Rate>, nonOrderRows: Array<any> = []): Array<LimitOrder> {
    if (!this.rateProcessingBusy) {
      this.rateProcessingBusy = true;
      let updatedRows: Array<LimitOrder> = Array.isArray(nonOrderRows) ? nonOrderRows : [];

      if (rates != null && rates.length > 0) {
        // Makes sure we don't update orders for the same pair
        let rateCodes: Set<string> = new Set(rates.map((rate: Rate) => rate.productCode));

        rateCodes.forEach((rateCode: string) => {
          let orderSet: Set<string> = this._pairOrderIds.get(rateCode);
          if (orderSet) {
            for (let id of orderSet) {
              let lo: LimitOrder = this._rawData[id];
              if (lo) {
                lo.tempStale = true;
                updatedRows.push(lo);
              }
            }
          }
        });
      }

      if (updatedRows.length > 0) {
        this.partialUpdateSubject.next(updatedRows);
      }

      setTimeout(this.checkRateQueue.bind(this), 1000);

      return updatedRows;
    } else {
      rates.forEach((rate) => {
        this.queuedRates.set(rate.key, rate);
      });
    }

    return [];
  }

  public getRowDataWithStaleTemp(): Array<LimitOrder> {
    return Object.keys(this._rawData).map((itemId: string) => {
      this._rawData[itemId].tempStale = true;
      return this._rawData[itemId];
    });
  }

  public getRowData(): Array<any> {
    return Object.keys(this._rawData).map((itemId: string) => this._rawData[itemId]);
  }

  protected checkRateQueue() {
    let rateArr: Array<Rate> = [];
    this.queuedRates.forEach((rate: Rate, key: string) => {
      rateArr.push(rate);
    });
    this.queuedRates.clear();
    this.rateProcessingBusy = false;
    if (rateArr.length > 0) {
      this.processRates(rateArr);
    }
  }

  protected setUserSettingsBlotterPreferences(config: any): void {
    if (config) {
      let savedSettings = JSON.parse(config);
      if (
        savedSettings.hasOwnProperty("pauseRatesModel") &&
        savedSettings.pauseRatesModel !== null &&
        savedSettings.pauseRatesModel > 0
      ) {
        BfxBlotterOrdersService.ratePauseTimeout = parseInt(savedSettings.pauseRatesModel);
      }
      BfxBlotterOrdersService.marketBarEnabled =
        savedSettings.hasOwnProperty("marketBar") &&
        savedSettings.marketBar !== null &&
        savedSettings.marketBar == "y"
          ? true
          : false;
    }
  }

  protected updateMarketBarStatus() {
    if (!BfxBlotterOrdersService.marketBarEnabled && this.marketBar != null) {
      this.deleteMarketBarNode();
    } else {
      this.createMarketBarNode();
    }
  }

  protected deleteMarketBarNode() {
    this.onDeleteEvent(this.marketBar.orderId);
  }

  protected createMarketBarNode() {
    this.marketBar = new FixedIndexRow(this.getMarketBar(this.getTableDefinition()));

    this.onFullUpdateEvent([this.marketBar]);
  }

  private getMarketBar(td: TableDefinition): any {
    let bar = {};
    const id = CubiqTS.Long.fromNumber(MARKET_BAR_ORDER_ID_KEY);

    bar[td.primaryKey] = id.toString();
    bar["orderId"] = id;
    bar["ordTemp"] = 0;
    bar["perc"] = 0;
    bar["percSort"] = 0;
    bar["tempStale"] = false;
    bar["fullWidth"] = true;
    bar["isMarketBar"] = true;

    return bar;
  }

  /**
   * Get an array of all other active orders that share any their strategy IDs with the input orders that are not
   * referenced in the input array
   *
   * @param rows An array of Order objects that must have an orderRef, strategyRef, and orderState property
   * @returns
   */
  public findRelatedOrders(
    rows: CubiqTS.OrderData[],
    excludeOrderState: Set<CubiqTS.OrderState> = new Set([...INVISIBLE_STATES, ...TERMINAL_STATES]),
  ): CubiqTS.OrderData[] {
    let relatedRows: CubiqTS.OrderData[] = [];

    let rowMap: any = this._rawData;
    /** Existing order refs that have been already included */
    let originMap: any = {};
    /** Required strategy IDs */
    let strategyRefMap: any = {};

    let strategyCount = 0;
    rows.forEach((row: any) => {
      if (row.strategyRef) {
        strategyCount++;
        originMap[row.orderRef] = true;
        strategyRefMap[row.strategyRef] = true;
      }
    });
    // If we have no strategies e.g. everythings a BM, then we stop processing
    if (strategyCount > 0) {
      for (let rowId in rowMap) {
        const rowData: CubiqTS.OrderData = rowMap[rowId];
        if (rowData.strategyRef && strategyRefMap[rowData.strategyRef]) {
          if (originMap[rowData.orderRef] == null && !excludeOrderState.has(rowData.orderState)) {
            originMap[rowData.orderRef] = true;
            relatedRows.push(rowData);
          }
        }
      }
    }
    return relatedRows;
  }
}
